import { CreditCard, BedDouble, Star, ShieldCheck, MapPin, Sparkles, Lock } from "lucide-react";
import { STRIPE_LINKS } from "@/lib/company";
import StripeBuyButton from "@/components/StripeBuyButton";

export default function TestDrivePage() {
  return (
    <div data-testid="testdrive-page" className="bg-[#020617]">
      {/* HERO */}
      <section className="relative overflow-hidden aios-starfield">
        <div className="absolute inset-0 aios-grid-overlay opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-transparent to-[#020617]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-28">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-red-600/40 bg-red-600/10 px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-red-500" />
              <span className="font-display text-xs tracking-[0.25em] text-red-400 font-bold uppercase">
                The Downsell · Risk-Free Test Drive
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white">
              Not Ready to Commit to a <br />
              <span className="text-red-500">Full Partnership?</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              Let us prove our infrastructure is 100% legitimate before you invest in a full corporate license.
              Test the speed and reality of our system right now with a single 3-Night Luxury Hotel Stay Voucher for just <span className="text-white font-semibold">$9.95 (Tax Included)</span>.
            </p>
          </div>
        </div>
      </section>

      {/* OFFER CARD */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Offer visual */}
          <div className="lg:col-span-2">
            <div className="relative aspect-[3/4] overflow-hidden border border-slate-800 sticky top-28">
              <img
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=900&q=80"
                alt="Luxury hotel suite — 3-night stay voucher"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/30 to-transparent" />
              <div className="absolute top-4 left-4 bg-red-600 px-3 py-1 font-display text-[10px] tracking-[0.25em] text-white font-bold">
                3 NIGHTS · LUXURY
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-1 text-yellow-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400" />
                  ))}
                </div>
                <div className="mt-2 font-display font-bold text-white text-2xl tracking-tight">
                  Luxury Hotel Stay Voucher
                </div>
                <div className="flex items-center gap-1 text-slate-300 text-sm mt-1">
                  <MapPin className="w-3 h-3" /> Nationwide property network
                </div>
              </div>
            </div>
          </div>

          {/* Offer details + checkout */}
          <div className="lg:col-span-3">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tighter">
              The <span className="text-red-500">$9.95</span> 3-Night Luxury Hotel Stay Voucher
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed text-base md:text-lg">
              A complimentary three-night stay at a vetted luxury hotel from our nationwide property partner network.
              <span className="text-white font-semibold"> No strings attached. No contract commitments. No timeshare pitches.</span>
              We offer this single-use trial pass at our direct cost to prove our business model is completely transparent.
              The luxury room rate is covered in full; you or your traveler simply cover the standard nightly destination taxes at activation.
            </p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: BedDouble, label: "3 Luxury Nights" },
                { icon: ShieldCheck, label: "No Contract" },
                { icon: Star, label: "5-Star Network" },
              ].map(({ icon: Icon, label }, idx) => (
                <div
                  key={label}
                  data-testid={`testdrive-perk-${idx}`}
                  className="aios-card p-5 text-center"
                >
                  <div className="aios-card-accent" />
                  <Icon className="w-6 h-6 text-red-500 mx-auto" />
                  <div className="mt-3 font-display font-bold text-white text-sm tracking-wide">{label}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 border-l-2 border-red-600 bg-slate-900/40">
              <div className="font-display text-lg md:text-xl font-bold text-white tracking-tight">
                No strings attached. No contract commitments. No timeshare pitches.
              </div>
              <p className="text-slate-400 mt-2 text-sm leading-relaxed">
                This isn't a timeshare bait. You will not sit through a 90-minute sales presentation. You won't be cold-called by a closer.
                The voucher exists for one reason: to let you experience our network's quality risk-free, so the $485 portal decision later is obvious.
              </p>
            </div>

            {/* Pricing card */}
            <div
              data-testid="testdrive-checkout-card"
              className="mt-10 bg-white text-slate-900 border-t-4 border-red-600 shadow-[0_25px_80px_-15px_rgba(220,38,38,0.3)]"
            >
              <div className="p-8">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter">$9.95</span>
                  <span className="text-slate-500 font-medium">USD · one-time</span>
                </div>
                <p className="text-slate-600 text-sm mt-2">
                  Activates your 3-Night Luxury Hotel Stay Voucher. Taxes/resort fees paid directly to property at check-in.
                </p>

                <div className="mt-6" data-testid="testdrive-stripe-wrap">
                  <StripeBuyButton
                    buyButtonId="buy_btn_1TnYJC2Ck4RqpKdWlvwRbth9"
                    publishableKey="pk_live_51SIcAO2Ck4RqpKdWGR4SgW6FYKx72BX5XKlwBWIYPFtWn5ciIrg8QrJ1tG1CCCJMYqmZCWgj7axnWbee7146lxKc00noaWg8Gh"
                    testId="testdrive-stripe-btn"
                  />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
                  <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-red-600" /> No recurring billing</div>
                  <div className="flex items-center gap-1"><Lock className="w-4 h-4 text-red-600" /> Stripe Secured</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
